import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Tabs, message } from "antd";
import { useOrganization } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { Drawer } from "aha-components";
import BankTransfer from "./BankTransfer";
import OnlinePayment from "./OnlinePayment";
import PaymentSummary from "./PaymentSummary";
import AccountManagerSupportNotice from "./AccountManagerSupportNotice";
import TopupPaymentStatusModal, {
  TopupPaymentFailureMessage,
  TopupPaymentStatus,
} from "@/components/modal/TopupPaymentStatusModal";
import {
  OtherPaymentMethod,
  PaymentProvider,
  SavedPaymentMethod,
} from "./types";
import normalizePrice from "@/utils/normalizePrice";
import { useModalStore } from "@/hooks/useModalStore";
import { usePaymentStore } from "@/hooks/usePaymentStore";
import {
  createTopup,
  getAirwallexHppUrl,
  getPaymentUrl,
  getTopupPreview,
  getTopupStatus,
  TopupPreview,
} from "@/api/payment";
import { payPlaceTypeLists } from "@/components/config";
import { useQuery } from "@/hooks/useQuery";
import { isMobile } from "@/biz/tool";
import { MAX_TOPUP_AMOUNT, MIN_TOPUP_AMOUNT } from "./config";
import { init } from "@airwallex/components-sdk";

interface CampaignDetails { affiliateBudget?: number; budgetLimitMonthly?: number; }

type AirwallexPayments = {
  redirectToCheckout: (options: Record<string, unknown>) => void | string;
};

export default function ChargeModal() {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const { walletDetail, reQueryWalletData } = usePaymentStore();
  const balance = walletDetail?.balance ?? 0;
  const [amount, setAmount] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<SavedPaymentMethod | null>(null);
  const [amountError, setAmountError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [activePaymentTab, setActivePaymentTab] = useState("online");
  const [loading, setLoading] = useState(false);
  const [topupPreview, setTopupPreview] = useState<TopupPreview | null>(null);
  const [isTopupPreviewLoading, setIsTopupPreviewLoading] = useState(false);
  const [topupPaymentStatus, setTopupPaymentStatus] =
    useState<TopupPaymentStatus | null>(null);
  const [topupPaymentAmount, setTopupPaymentAmount] = useState<number | null>(
    null
  );
  const [topupPaymentFailureMessage, setTopupPaymentFailureMessage] =
    useState<TopupPaymentFailureMessage | null>(null);
  const topupPreviewRequestIdRef = useRef(0);
  const topupPaymentRequestIdRef = useRef(0);
  const campaignId = useQuery("campaignId");
  const {
    showChargeModal, setShowChargeModal, payPlace, campaignDetails,
    chargeModalInfo, setChargeModalInfo, setPayPlace,
  } = useModalStore();

  useEffect(() => {
    if (!showChargeModal) return;

    reQueryWalletData();
  }, [reQueryWalletData, showChargeModal]);

  const validateAmount = useCallback(
    (value: number | null) => {
      if (value === null) return "";

      if (
        !Number.isFinite(value) ||
        value < MIN_TOPUP_AMOUNT ||
        value > MAX_TOPUP_AMOUNT
      ) {
        return t("components.chargeModal.checkoutValidation.amountOutOfRange", {
          min: normalizePrice(MIN_TOPUP_AMOUNT),
          max: normalizePrice(MAX_TOPUP_AMOUNT),
          defaultValue: "Amount must be between {min} and {max}.",
        });
      }

      if (chargeModalInfo?.amount && value < chargeModalInfo.amount) {
        return t("components.chargeModal.checkoutValidation.minimumAmount", {
          amount: normalizePrice(chargeModalInfo.amount),
          defaultValue: "Please enter at least {amount}.",
        });
      }

      return "";
    },
    [chargeModalInfo?.amount, t]
  );

  const handleAmountChange = useCallback(
    (value: number | null) => {
      setAmount(value);
      setAmountError(validateAmount(value));
    },
    [validateAmount]
  );

  useEffect(() => {
    if (chargeModalInfo?.amount) handleAmountChange(chargeModalInfo.amount);
  }, [chargeModalInfo?.amount, handleAmountChange]);

  useEffect(() => {
    if (payPlace === payPlaceTypeLists.ORDER_LIST && showChargeModal) {
      const { affiliateBudget = 0, budgetLimitMonthly = 0 } = (campaignDetails || {}) as CampaignDetails;
      if (affiliateBudget || budgetLimitMonthly) {
        handleAmountChange(affiliateBudget + budgetLimitMonthly);
      }
    }
  }, [campaignDetails, handleAmountChange, payPlace, showChargeModal]);

  // 弹窗也可能由全局状态在非 handleClose 路径下关闭。
  // 关闭后统一清空表单状态，避免下次打开仍显示旧的选卡或金额。
  useEffect(() => {
    if (showChargeModal) return;

    setAmount(null);
    setPaymentMethod(null);
    setAmountError("");
    setPaymentError("");
    setActivePaymentTab("online");
    setTopupPreview(null);
    setIsTopupPreviewLoading(false);
    topupPreviewRequestIdRef.current += 1;
    setChargeModalInfo({ amount: null });
    setPayPlace("");
  }, [setChargeModalInfo, setPayPlace, showChargeModal]);

  useEffect(() => {
    const paymentMethodId = paymentMethod?.paymentMethodId;
    const canPreview =
      showChargeModal &&
      activePaymentTab === "online" &&
      typeof amount === "number" &&
      !validateAmount(amount) &&
      Boolean(paymentMethodId);

    if (!canPreview || !paymentMethodId) {
      topupPreviewRequestIdRef.current += 1;
      setTopupPreview(null);
      setIsTopupPreviewLoading(false);
      return;
    }

    const requestId = topupPreviewRequestIdRef.current + 1;
    topupPreviewRequestIdRef.current = requestId;
    let isCurrentRequest = true;
    setTopupPreview(null);
    setIsTopupPreviewLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const preview = await getTopupPreview({
          paymentMethod: {
            type: paymentMethod.provider === "airwallex" ? "awx" : "stripe",
            id: paymentMethodId,
          },
          amount,
        });

        if (isCurrentRequest && topupPreviewRequestIdRef.current === requestId) {
          setTopupPreview(preview);
        }
      } catch {
        // 输入变化时不弹出全局错误；保留空摘要，用户可继续修改金额或支付方式。
      } finally {
        if (isCurrentRequest && topupPreviewRequestIdRef.current === requestId) {
          setIsTopupPreviewLoading(false);
        }
      }
    }, 300);

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(timer);
    };
  }, [
    activePaymentTab,
    amount,
    paymentMethod?.paymentMethodId,
    paymentMethod?.provider,
    showChargeModal,
    validateAmount,
  ]);

  useEffect(
    () => () => {
      topupPaymentRequestIdRef.current += 1;
    },
    []
  );

  const buildReturnUrl = (
    provider: "awx",
    checkoutAmount: number
  ) => {
    const url = new URL(window.location.href);
    const [hashPath, hashSearch = ""] = (url.hash || "#/payment").split("?");
    const hashParams = new URLSearchParams(hashSearch);
    hashParams.set("from", provider);
    hashParams.set("amount", String(checkoutAmount));
    if (payPlace) hashParams.set("payPlace", payPlace);
    if (campaignId) hashParams.set("campaignId", campaignId);
    url.hash = `${hashPath}?${hashParams.toString()}`;
    return url.toString();
  };

  const createIdempotencyKey = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  };

  // Stripe HPP 必须保持原有的回跳 URL 与请求参数约定。HPP 的结果弹窗
  // 仅替换展示样式，不应改变后端生成或解析回跳结果的流程。
  const buildStripeHppReturnUrl = (checkoutAmount: number) => {
    const separator = window.location.href.includes("?") ? "&" : "?";
    return `${window.location.href}${separator}amount=${checkoutAmount}&payPlace=${payPlace}&campaignId=${campaignId}`;
  };

  const getTopupStatusValue = (result: any) =>
    String(result?.status || result?.paymentStatus || "").toLowerCase();

  const isTopupSuccessful = (result: any) => {
    const status = getTopupStatusValue(result);
    return result?.success === true || ["success", "succeeded", "completed"].includes(status);
  };

  const isTopupFailed = (result: any) => {
    const status = getTopupStatusValue(result);
    return ["failed", "cancelled", "canceled", "expired"].includes(status);
  };

  const pollTopupStatus = async (transactionId: string, requestId: number) => {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 1_000));

      if (topupPaymentRequestIdRef.current !== requestId) return;

      try {
        const result = await getTopupStatus(transactionId);
        if (topupPaymentRequestIdRef.current !== requestId) return;

        if (isTopupSuccessful(result)) {
          usePaymentStore.getState().reQueryWalletData({ andLoading: false });
          setTopupPaymentStatus("success");
          return;
        }
        if (isTopupFailed(result)) {
          setTopupPaymentFailureMessage(result?.failure?.message || null);
          setTopupPaymentStatus("failed");
          return;
        }
      } catch {
        setTopupPaymentStatus("failed");
        return;
      }
    }

    setTopupPaymentStatus("failed");
  };

  const handleSavedPaymentMethodCheckout = async (
    checkoutAmount: number,
    method: SavedPaymentMethod
  ) => {
    if (!method.paymentMethodId) return;

    const requestId = topupPaymentRequestIdRef.current + 1;
    topupPaymentRequestIdRef.current = requestId;
    setLoading(true);
    setTopupPaymentAmount(checkoutAmount);
    setTopupPaymentFailureMessage(null);
    setTopupPaymentStatus("processing");
    // 支付结果使用独立状态弹窗展示，提交后不保留 Add funds 弹窗作为背景。
    setShowChargeModal(false);
    try {
      const result = await createTopup(
        {
          paymentMethod: {
            type: method.provider === "airwallex" ? "awx" : "stripe",
            id: method.paymentMethodId,
          },
          amount: checkoutAmount,
        },
        createIdempotencyKey()
      );

      if (!result?.transactionId) {
        setTopupPaymentStatus("failed");
        return;
      }

      void pollTopupStatus(result.transactionId, requestId);
    } catch {
      setTopupPaymentStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtherPaymentMethodSelect = async (
    method: OtherPaymentMethod,
    provider: PaymentProvider
  ) => {
    const amountValidationError =
      amount === null
        ? t("components.chargeModal.checkoutValidation.amountRequired", {
            defaultValue: "Please select or enter an amount to continue.",
          })
        : validateAmount(amount);
    if (amountValidationError) {
      setAmountError(amountValidationError);
      return;
    }

    const checkoutAmount = amount as number;
    const type = provider === "stripe" ? "stripe" : "awx";

    try {
      if (type === "stripe") {
        const result = await getPaymentUrl({
          amount: checkoutAmount,
          payPlace,
          url: buildStripeHppReturnUrl(checkoutAmount),
        });
        const url = result?.url || result?.redirectUrl;
        if (!url) throw new Error("Missing Stripe checkout URL");
        window.location.replace(url);
        return;
      }

      const result = await getAirwallexHppUrl(
        {
          methodType: method.methodType,
          amount: checkoutAmount,
          url: buildReturnUrl("awx", checkoutAmount),
        },
        createIdempotencyKey()
      );
      const action = result?.action;
      if (
        action?.type !== "airwallex_hpp" ||
        !action.env ||
        !action.checkoutOptions
      ) {
        throw new Error("Invalid Airwallex HPP response");
      }

      const { payments } = (await init({
        env: action.env as any,
        enabledElements: ["payments"],
      })) as unknown as { payments?: AirwallexPayments };

      if (!payments) throw new Error("Airwallex Payments SDK is unavailable");

      const redirectError = payments.redirectToCheckout(action.checkoutOptions);
      if (typeof redirectError === "string") throw new Error(redirectError);
    } catch {
      message.error(
        t("components.chargeModal.checkoutValidation.failedToStartPayment", {
          defaultValue: "Unable to start payment. Please try again.",
        })
      );
    }
  };

  const handleCheckout = async () => {
    const nextAmountError =
      amount === null
        ? t("components.chargeModal.checkoutValidation.amountRequired", {
            defaultValue: "Please select or enter an amount to continue.",
          })
        : validateAmount(amount);
    const nextPaymentError = paymentMethod
      ? ""
      : t("components.chargeModal.checkoutValidation.paymentMethodRequired", {
          defaultValue: "Please select a payment method to continue.",
        });
    setAmountError(nextAmountError);
    setPaymentError(nextPaymentError);
    if (nextAmountError || nextPaymentError) return;
    const checkoutAmount = amount as number;
    await handleSavedPaymentMethodCheckout(checkoutAmount, paymentMethod!);
  };

  const handlePaymentMethodChange = useCallback(
    (method: SavedPaymentMethod | null) => {
      setPaymentMethod(method);
      setPaymentError("");
    },
    []
  );

  const handleClose = () => {
    setShowChargeModal(false);
    setAmount(null);
    setPaymentMethod(null);
    setAmountError("");
    setPaymentError("");
    setActivePaymentTab("online");
    setTopupPreview(null);
    setIsTopupPreviewLoading(false);
    topupPaymentRequestIdRef.current += 1;
    setTopupPaymentStatus(null);
    setTopupPaymentAmount(null);
    setTopupPaymentFailureMessage(null);
    setChargeModalInfo({ amount: null });
    setPayPlace("");
  };

  const leftContent = (
    <div className="charge-modal-left-content min-h-full bg-white px-[21px] pb-6 pt-4 md:px-8 md:py-6">
      <div className="hidden pr-8 md:block">
        <h1 className="text-2xl font-semibold text-[#171717]">
          {t("components.chargeModal.title", { defaultValue: "Add funds" })}
        </h1>
        <p className="mt-1 text-sm text-[#737373]">
          {t("components.chargeModal.availableBalance", { defaultValue: "Available balance" })} {normalizePrice(balance)} ·{" "}
          {organization?.name ?? "Aha-test"}
        </p>
      </div>
      <div className="mt-0 md:mt-7"><AccountManagerSupportNotice /></div>
      <Tabs
        className="charge-modal-tabs mt-5 md:mt-6"
        activeKey={activePaymentTab}
        onChange={setActivePaymentTab}
        items={[
          { key: "online", label: t("components.chargeModal.onlinePayment", { defaultValue: "Online Payment" }), children: <OnlinePayment isOpen={showChargeModal} amount={amount} selectedPaymentMethod={paymentMethod} minimumAmount={chargeModalInfo?.amount ?? undefined} amountError={amountError} paymentError={paymentError} onAmountChange={handleAmountChange} onPaymentMethodChange={handlePaymentMethodChange} otherPaymentMethods={walletDetail?.otherPaymentMethods} onOtherPaymentMethodSelect={handleOtherPaymentMethodSelect} /> },
          { key: "bank", label: t("components.chargeModal.bankTransfer1", { defaultValue: "Bank Transfer" }), children: <BankTransfer /> },
        ]}
      />
    </div>
  );

  const content = (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden md:grid ${activePaymentTab === "online" ? "md:grid-cols-[590px_410px]" : "md:grid-cols-1"}`}>
      <div className={`charge-modal-form scrollable-y min-h-0 flex-1 overflow-y-auto border-b border-[#E7E7E7] bg-white md:border-b-0 ${activePaymentTab === "online" ? "md:border-r" : ""}`}>
        {leftContent}
        {activePaymentTab === "online" && (
          <div className="md:hidden">
            <PaymentSummary
              balance={balance}
              paymentMethod={paymentMethod}
              preview={topupPreview}
              previewLoading={isTopupPreviewLoading}
              loading={loading}
              onCheckout={handleCheckout}
              variant="mobile-details"
            />
          </div>
        )}
      </div>
      {activePaymentTab === "online" && (
        <div className="hidden min-h-[520px] md:block"><PaymentSummary balance={balance} paymentMethod={paymentMethod} preview={topupPreview} previewLoading={isTopupPreviewLoading} loading={loading} onCheckout={handleCheckout} /></div>
      )}
      {activePaymentTab === "online" && (
        <div className="shrink-0 md:hidden">
          <PaymentSummary
            balance={balance}
            paymentMethod={paymentMethod}
            preview={topupPreview}
            previewLoading={isTopupPreviewLoading}
            loading={loading}
            onCheckout={handleCheckout}
            variant="mobile-footer"
          />
        </div>
      )}
    </div>
  );

  if (isMobile()) {
    return (
      <>
        <Drawer
          title={
            <div>
              <h1 className="text-lg font-semibold leading-7 text-[#171717]">
                {t("components.chargeModal.title", { defaultValue: "Add funds" })}
              </h1>
              <p className="mt-0.5 text-xs font-normal leading-4 text-[#737373]">
                {t("components.chargeModal.availableBalance", { defaultValue: "Available balance" })} {normalizePrice(balance)} · {organization?.name ?? "Aha-test"}
              </p>
            </div>
          }
          open={showChargeModal}
          onClose={handleClose}
          placement="bottom"
          width="100vw"
          height="100vh"
          drawerStyle={{ background: "#FFFFFF" }}
          headerStyle={{
            minHeight: 76,
            padding: "16px 21px 12px",
            borderBottom: "none",
          }}
          bodyStyle={{ padding: 0, background: "#FFFFFF", overflow: "hidden" }}
        >
          {content}
        </Drawer>
        <TopupPaymentStatusModal
          status={topupPaymentStatus}
          amount={topupPaymentAmount}
          failureMessage={topupPaymentFailureMessage}
          onBack={handleClose}
        />
      </>
    );
  }

  return (
    <>
      <Modal open={showChargeModal} onCancel={handleClose} footer={null} title={null} width={1000} centered styles={{ content: { padding: 0, overflow: "hidden", borderRadius: 14, background: "#FAFAF7" }, body: { height: "min(806px, 90vh)" } }}>
        {content}
      </Modal>
      <TopupPaymentStatusModal
        status={topupPaymentStatus}
        amount={topupPaymentAmount}
        failureMessage={topupPaymentFailureMessage}
        onBack={handleClose}
      />
    </>
  );
}
